/**
 * GAMIFICATION ADMIN PANEL — Painel de Governança de Gamificação
 *
 * Painel de configuração das regras de pontuação, disciplina operacional,
 * multiplicadores de fluxo e escala de autoridade por Belt.
 *
 * Design: Enterprise B2B — tipografia densa, bordas finas, sem saturação excessiva.
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart2,
  Shield,
  Clock,
  Award,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { GamificationService, type GamificationRules } from '../services/gamificationService';

// ---------------------------------------------------------------------------
// Tipos locais
// ---------------------------------------------------------------------------

interface GamificationAdminPanelProps {
  organizationId: string | null;
  organizationName?: string;
}

type MessageState = { type: 'success' | 'error' | 'warning'; text: string } | null;

// ---------------------------------------------------------------------------
// Primitivas de UI
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <div className="bg-white dark:bg-[var(--bg-surface)] border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
      <span className="text-slate-500 dark:text-slate-400">{icon}</span>
      <div>
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide">{title}</p>
        {subtitle && <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-1">
      {label}
    </label>
    {children}
    {hint && <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 leading-relaxed">{hint}</p>}
  </div>
);

const NumberInput: React.FC<{
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
}> = ({ value, onChange, step = 1, min, max, suffix }) => (
  <div className="relative flex items-center">
    <input
      type="number"
      step={step}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0)}
      className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 dark:border-slate-600 rounded
                 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200
                 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                 transition-colors"
    />
    {suffix && (
      <span className="absolute right-3 text-[11px] font-semibold text-slate-400 pointer-events-none">
        {suffix}
      </span>
    )}
  </div>
);

const Toggle: React.FC<{
  enabled: boolean;
  onChange: (v: boolean) => void;
  labelOn: string;
  labelOff: string;
}> = ({ enabled, onChange, labelOn, labelOff }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className={`flex items-center gap-2 px-3 py-1.5 rounded border text-[11px] font-semibold transition-colors ${
      enabled
        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300'
        : 'bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'
    }`}
  >
    <div className={`w-7 h-4 rounded-full relative transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
      <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
    </div>
    {enabled ? labelOn : labelOff}
  </button>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export const GamificationAdminPanel: React.FC<GamificationAdminPanelProps> = ({
  organizationId,
  organizationName,
}) => {
  const [rules, setRules] = useState<GamificationRules | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<MessageState>(null);

  useEffect(() => {
    if (organizationId && organizationId !== 'dev-org') {
      loadRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const loadRules = async () => {
    if (!organizationId || loading || organizationId === 'dev-org') return;
    setLoading(true);
    setMessage(null);
    try {
      const data = await GamificationService.getGamificationRules(organizationId);
      if (data) {
        setRules(data);
      } else {
        const defaults: Partial<GamificationRules> = {
          kpi_bonus_leader: 1000,
          kpi_bonus_team: 300,
          solidarity_enabled: true,
          andon_penalty_points: -50,
          recovery_plan_deadline_hours: 48,
          recovery_plan_bonus: 100,
          deadline_multiplier_enabled: true,
          deadline_multiplier_value: 1.2,
          deadline_compliance_threshold: 100,
          multiplier_duration_days: 7,
          team_weight_percentage: 30,
          bronze_min_points: 0,
          silver_min_points: 1000,
          gold_min_points: 5000,
          black_belt_min_points: 15000,
        };
        const ok = await GamificationService.updateGamificationRules(organizationId, defaults);
        setRules(defaults as GamificationRules);
        if (!ok) {
          setMessage({
            type: 'warning',
            text: 'Permissoes RLS nao configuradas. Execute fix_gamification_rls.sql no Supabase para persistir alteracoes.',
          });
        }
      }
    } catch {
      setMessage({ type: 'error', text: 'Erro ao carregar regras. Verifique as permissoes no Supabase.' });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!organizationId || !rules) return;
    setSaving(true);
    setMessage(null);
    const ok = await GamificationService.updateGamificationRules(organizationId, rules);
    if (ok) {
      setMessage({ type: 'success', text: 'Regras de pontuacao salvas com sucesso.' });
      setTimeout(() => setMessage(null), 4000);
    } else {
      setMessage({ type: 'error', text: 'Falha ao salvar. Execute fix_gamification_rls.sql no Supabase para corrigir permissoes RLS.' });
      setTimeout(() => setMessage(null), 6000);
    }
    setSaving(false);
  };

  const set = <K extends keyof GamificationRules>(key: K, value: GamificationRules[K]) => {
    if (!rules) return;
    setRules({ ...rules, [key]: value });
  };

  // ---- Estados especiais -----------------------------------------------

  if (!organizationId || organizationId === 'dev-org') {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {organizationId === 'dev-org'
              ? 'Gamificacao desabilitada em modo desenvolvimento. Crie uma organizacao real para configurar.'
              : 'Selecione uma organizacao para gerenciar as regras de gamificacao.'}
          </p>
        </div>
      </div>
    );
  }

  if (loading || !rules) {
    return (
      <div className="p-6 flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-slate-400" />
      </div>
    );
  }

  // ---- Render principal --------------------------------------------------

  return (
    <div className="p-6 max-w-[1040px] mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Governanca de Gamificacao
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            {organizationName ?? 'Organizacao'} — Regras de pontuacao e niveis de autoridade
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 dark:bg-slate-100 dark:hover:bg-slate-300 dark:text-slate-900
                     disabled:opacity-50 text-white text-xs font-bold rounded transition-colors shadow-sm"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {saving ? 'Salvando...' : 'Salvar Alteracoes'}
        </button>
      </div>

      {/* Feedback */}
      {message && (
        <div className={`flex items-start gap-3 p-3 rounded border text-xs ${
          message.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300'
            : message.type === 'warning'
            ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300'
            : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'
        }`}>
          {message.type === 'success' && <CheckCircle2 size={14} className="shrink-0 mt-0.5" />}
          {message.type === 'error'   && <XCircle size={14} className="shrink-0 mt-0.5" />}
          {message.type === 'warning' && <AlertTriangle size={14} className="shrink-0 mt-0.5" />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* ================================================================
          Card 1 — Metas de KPIs e Lideranca
      ================================================================ */}
      <SectionCard
        icon={<BarChart2 size={15} />}
        title="Metas de KPIs e Lideranca"
        subtitle="Pontuacao atribuida ao lider e a equipe quando uma meta de KPI e atingida"
      >
        <div className="grid grid-cols-3 gap-5">
          <Field label="Bonus do Lider" hint="Pontos creditados ao lider do projeto na conclusao da meta.">
            <NumberInput
              value={rules.kpi_bonus_leader}
              onChange={(v) => set('kpi_bonus_leader', v)}
              suffix="pts"
            />
          </Field>
          <Field label="Bonus da Equipe" hint="Pontos base distribuidos a cada membro participante.">
            <NumberInput
              value={rules.kpi_bonus_team}
              onChange={(v) => set('kpi_bonus_team', v)}
              suffix="pts"
            />
          </Field>
          <Field
            label={`Peso da Equipe — ${rules.team_weight_percentage}%`}
            hint="Percentual do bonus base que cada membro recebe em relacao ao lider."
          >
            <div className="flex items-center gap-3 pt-1">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={rules.team_weight_percentage}
                onChange={(e) => set('team_weight_percentage', parseInt(e.target.value))}
                className="flex-1 h-1.5 rounded accent-blue-600 cursor-pointer"
              />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-300 w-10 text-right tabular-nums">
                {rules.team_weight_percentage}%
              </span>
            </div>
          </Field>
        </div>
      </SectionCard>

      {/* ================================================================
          Card 2 — Disciplina Operacional
      ================================================================ */}
      <SectionCard
        icon={<Shield size={15} />}
        title="Disciplina Operacional — Demerito Solidario"
        subtitle="Penalizacoes e bonus de recuperacao ao acionamento do Andon de sustentacao"
      >
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            {rules.solidarity_enabled
              ? 'Ativo: a equipe perde pontos de disciplina quando um Kaizen entra em Andon.'
              : 'Inativo: apenas o responsavel direto sofre penalizacao.'}
          </p>
          <Toggle
            enabled={rules.solidarity_enabled}
            onChange={(v) => set('solidarity_enabled', v)}
            labelOn="Ativo"
            labelOff="Inativo"
          />
        </div>

        <div className={`grid grid-cols-3 gap-5 transition-opacity ${rules.solidarity_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <Field
            label="Penalidade por Andon"
            hint="Valor negativo — pontos descontados do total ao acionar o Andon."
          >
            <NumberInput
              value={rules.andon_penalty_points}
              onChange={(v) => set('andon_penalty_points', v)}
              suffix="pts"
            />
          </Field>
          <Field
            label="Prazo para Plano de Recuperacao"
            hint="Janela de tempo, em horas, para a equipe abrir o plano e cessar a penalizacao."
          >
            <NumberInput
              value={rules.recovery_plan_deadline_hours}
              onChange={(v) => set('recovery_plan_deadline_hours', v)}
              suffix="h"
              min={1}
            />
          </Field>
          <Field
            label="Bonus de Reacao Proativa"
            hint="Pontos concedidos ao abrir o plano de recuperacao dentro do prazo."
          >
            <NumberInput
              value={rules.recovery_plan_bonus}
              onChange={(v) => set('recovery_plan_bonus', v)}
              suffix="pts"
            />
          </Field>
        </div>
      </SectionCard>

      {/* ================================================================
          Card 3 — Multiplicadores de Fluxo Continuo
      ================================================================ */}
      <SectionCard
        icon={<Clock size={15} />}
        title="Multiplicadores de Fluxo Continuo"
        subtitle="Fator de amplificacao de pontos ativado quando a equipe cumpre os prazos sistematicamente"
      >
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            {rules.deadline_multiplier_enabled
              ? 'Ativo: multiplicador aplicado ao atingir o limiar de conformidade de prazo.'
              : 'Inativo: sem amplificacao de pontos por conformidade de prazo.'}
          </p>
          <Toggle
            enabled={rules.deadline_multiplier_enabled}
            onChange={(v) => set('deadline_multiplier_enabled', v)}
            labelOn="Ativo"
            labelOff="Inativo"
          />
        </div>

        <div className={`grid grid-cols-3 gap-5 transition-opacity ${rules.deadline_multiplier_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <Field
            label="Fator Multiplicador"
            hint="Ex: 1.2 aplica +20% sobre todos os pontos do periodo ativo."
          >
            <NumberInput
              value={rules.deadline_multiplier_value}
              onChange={(v) => set('deadline_multiplier_value', v)}
              step={0.1}
              min={1.0}
              max={3.0}
              suffix="x"
            />
          </Field>
          <Field
            label="Conformidade Minima"
            hint="Percentual minimo de tarefas entregues no prazo para ativar o multiplicador de grupo."
          >
            <NumberInput
              value={rules.deadline_compliance_threshold}
              onChange={(v) => set('deadline_compliance_threshold', v)}
              min={50}
              max={100}
              step={5}
              suffix="%"
            />
          </Field>
          <Field
            label="Duracao do Multiplicador"
            hint="Quantos dias o multiplicador permanece ativo apos ser ativado."
          >
            <NumberInput
              value={rules.multiplier_duration_days}
              onChange={(v) => set('multiplier_duration_days', v)}
              min={1}
              suffix="dias"
            />
          </Field>
        </div>
      </SectionCard>

      {/* ================================================================
          Card 4 — Niveis de Autoridade (Regua de Belts)
      ================================================================ */}
      <SectionCard
        icon={<Award size={15} />}
        title="Niveis de Autoridade — Regua de Belts"
        subtitle="Pontuacao minima para progressao de cada nivel de autoridade Lean"
      >
        <div className="grid grid-cols-4 gap-5">
          <Field label="Bronze Belt" hint="Nivel de entrada. Todos os usuarios iniciam aqui.">
            <NumberInput
              value={rules.bronze_min_points}
              onChange={(v) => set('bronze_min_points', v)}
              suffix="pts"
            />
          </Field>
          <Field label="Silver Belt" hint="Padrao enterprise: 1.000 pts minimos.">
            <NumberInput
              value={rules.silver_min_points}
              onChange={(v) => set('silver_min_points', v)}
              suffix="pts"
            />
          </Field>
          <Field label="Gold Belt" hint="Padrao enterprise: 5.000 pts minimos.">
            <NumberInput
              value={rules.gold_min_points}
              onChange={(v) => set('gold_min_points', v)}
              suffix="pts"
            />
          </Field>
          <Field label="Black Belt" hint="Padrao enterprise: 15.000 pts minimos.">
            <NumberInput
              value={rules.black_belt_min_points}
              onChange={(v) => set('black_belt_min_points', v)}
              suffix="pts"
            />
          </Field>
        </div>

        {/* Regua visual de progressao */}
        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-3">
            Regua de Progressao
          </p>
          <div className="relative h-2 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700">
            <div className="absolute inset-y-0 left-0 w-1/4 bg-orange-400 rounded-l-full" />
            <div className="absolute inset-y-0 left-1/4 w-1/4 bg-slate-400" />
            <div className="absolute inset-y-0 left-2/4 w-1/4 bg-yellow-500" />
            <div className="absolute inset-y-0 left-3/4 w-1/4 bg-slate-800 dark:bg-slate-300 rounded-r-full" />
          </div>
          <div className="grid grid-cols-4 mt-1.5">
            {([
              { label: 'Bronze', pts: rules.bronze_min_points },
              { label: 'Silver', pts: rules.silver_min_points },
              { label: 'Gold',   pts: rules.gold_min_points },
              { label: 'Black',  pts: rules.black_belt_min_points },
            ] as const).map(({ label, pts }) => (
              <div key={label} className="text-center">
                <p className="text-[10px] font-bold text-slate-600 dark:text-slate-400">{label}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 tabular-nums">
                  {pts.toLocaleString('pt-BR')} pts
                </p>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Rodape de isolamento */}
      <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
        <Shield size={14} className="text-slate-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
          <strong className="text-slate-700 dark:text-slate-300">Isolamento por organizacao:</strong>{" "}
          As regras acima sao exclusivas desta organizacao. Pontos e rankings nao cruzam dados
          entre clientes distintos. O tenant isolation e garantido por RLS no Supabase.
        </p>
      </div>

    </div>
  );
};

export default GamificationAdminPanel;
